import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Search, MapPin, Tag } from "lucide-react";

const CarpetLogo = ({ className }) => (
  <svg className={className} viewBox="0 0 40 40" fill="none">
    <rect x="4" y="6" width="32" height="28" rx="3" fill="#F97316" />
    <rect x="8" y="10" width="24" height="20" rx="2" fill="white" />
    <rect x="12" y="14" width="16" height="12" rx="1" fill="#F97316" />
    <circle cx="20" cy="20" r="3" fill="white" />
  </svg>
);

const BLOG_POSTS = [
  {
    slug: "beylikduzu-hali-yikama",
    title: "Beylikdüzü Halı Yıkama – Profesyonel ve Hijyenik Çözümler",
    excerpt: "Beylikdüzü'nde halınızı hijyenik ve temiz tutun. Kapıdan alım teslim, uygun fiyat ve hızlı teslimat.",
    image: "https://images.unsplash.com/photo-1558317374-067fb5f30001?w=800&q=80",
    tags: ["beylikdüzü halı yıkama", "halı temizliği", "profesyonel halı yıkama"],
    ilce: "Beylikdüzü",
    date: "2026"
  },
  {
    slug: "esenyurt-hali-yikama",
    title: "Esenyurt Halı Yıkama – Halılarınızı Temiz ve Hijyenik Tutmanın Yolu",
    excerpt: "Esenyurt'ta profesyonel halı yıkama hizmeti. Halılarınızı profesyonel şekilde temizletin, lekelerde bile mükemmel sonuç.",
    image: "https://images.unsplash.com/photo-1600166898405-da9535204843?w=800&q=80",
    tags: ["esenyurt halı yıkama", "halı temizliği", "leke çıkarma"],
    ilce: "Esenyurt",
    date: "2026"
  },
  {
    slug: "avcilar-hali-yikama",
    title: "Avcılar Halı Yıkama – Profesyonel ve Hijyenik Çözümler",
    excerpt: "Avcılar'da halı yıkama hizmeti ile halılarınızı profesyonel şekilde temizletin. Hijyenik, ferah ve temiz halılar.",
    image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800&q=80",
    tags: ["avcılar halı yıkama", "halı temizliği", "hijyenik halı temizliği"],
    ilce: "Avcılar",
    date: "2026"
  },
  {
    slug: "buyukcekmeceye-hali-yikama",
    title: "Büyükçekmece Halı Yıkama – Kapıdan Alım Teslim Hizmeti",
    excerpt: "Büyükçekmece'de profesyonel halı yıkama. Adresinizden alıyor, tertemiz teslim ediyoruz.",
    image: "https://images.unsplash.com/photo-1527515545081-5db817172677?w=800&q=80",
    tags: ["büyükçekmece halı yıkama", "kapıdan halı yıkama", "profesyonel temizlik"],
    ilce: "Büyükçekmece",
    date: "2026"
  }
];

const BlogPage = () => {
  const [search, setSearch] = useState("");
  const [selectedIlce, setSelectedIlce] = useState("Tümü");

  const ilceler = ["Tümü", "Beylikdüzü", "Esenyurt", "Avcılar", "Büyükçekmece"];

  const filtered = BLOG_POSTS.filter(post => {
    const matchSearch = post.title.toLowerCase().includes(search.toLowerCase()) ||
      post.tags.some(t => t.toLowerCase().includes(search.toLowerCase()));
    const matchIlce = selectedIlce === "Tümü" || post.ilce === selectedIlce;
    return matchSearch && matchIlce;
  });

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Blog | Halı Yıkama Rehberi | Halıyol</title>
        <meta name="description" content="Halı yıkama hakkında her şey. İstanbul ilçelerine özel halı yıkama rehberleri, fiyatlar ve ipuçları." />
        <link rel="canonical" href="https://www.haliyol.com/blog" />
        <meta property="og:title" content="Blog | Halı Yıkama Rehberi | Halıyol" />
        <meta property="og:url" content="https://www.haliyol.com/blog" />
      </Helmet>

      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <CarpetLogo className="w-9 h-9" />
            <span className="text-xl font-bold text-slate-800">HALIYOL</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/">
              <Button variant="ghost" className="gap-2 text-slate-600">
                <ArrowLeft className="w-4 h-4" />
                Ana Sayfa
              </Button>
            </Link>
            <a href="tel:02126990301">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-5">
                Hemen Ara
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-orange-50 to-white py-14 px-4 border-b border-orange-100">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-800 mb-4">Bloglar</h1>
          <p className="text-lg text-slate-500 max-w-2xl">
            Halı yıkama hakkında her şeyi burada bulabilirsiniz. İlçenizi seçin, size özel rehberi okuyun.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 px-4 border-b border-slate-100 bg-white sticky top-16 z-40 shadow-sm">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <MapPin className="w-4 h-4" />
              <span>İlçe:</span>
            </div>
            {ilceler.map(ilce => (
              <button
                key={ilce}
                onClick={() => setSelectedIlce(ilce)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedIlce === ilce
                    ? "bg-orange-500 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-orange-50 hover:text-orange-600"
                }`}
              >
                {ilce}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Bloglarda ara..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 rounded-full border border-slate-200 text-sm focus:outline-none focus:border-orange-400 w-64"
            />
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-3">
          <span className="text-sm text-slate-400">{filtered.length} blog listeleniyor</span>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-2 gap-8">
          {filtered.map((post, i) => (
            <Link
              key={i}
              to={`/blog/${post.slug}`}
              className="group bg-white rounded-2xl border border-slate-100 hover:border-orange-200 hover:shadow-lg transition-all overflow-hidden"
            >
              <div className="aspect-[16/9] overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <div className="flex flex-wrap gap-2 mb-3">
                  {post.tags.slice(0, 3).map((tag, j) => (
                    <span key={j} className="text-xs bg-orange-50 text-orange-600 px-3 py-1 rounded-full font-medium">
                      {tag}
                    </span>
                  ))}
                </div>
                <h2 className="text-xl font-bold text-slate-800 mb-2 group-hover:text-orange-500 transition-colors leading-snug">
                  {post.title}
                </h2>
                <p className="text-slate-500 text-sm leading-relaxed mb-4">{post.excerpt}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {post.ilce}
                  </span>
                  <span className="text-orange-500 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                    Devamını Oku <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-orange-50">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-slate-800 mb-4">Halınızı Yıkatmaya Hazır mısınız?</h2>
          <p className="text-slate-600 mb-8">Hemen sipariş oluşturun, ekibimiz sizinle iletişime geçsin.</p>
          <a href="https://wa.me/902126990301?text=Merhaba%2C%20hal%C4%B1%20y%C4%B1kama%20hizmeti%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum." target="_blank" rel="noopener noreferrer">
            <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-10 py-6 text-lg font-semibold">
              WhatsApp'tan Ulaşın
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 px-4 text-center">
        <p className="text-slate-400 text-sm">&copy; 2026 HALIYOL. Tüm hakları saklıdır.</p>
      </footer>
    </div>
  );
};

export default BlogPage;
